import {
  createTaskRequestSchema,
  normalizeTaskTitle,
  type TaskDetail,
  type TaskPerson,
  type TaskStatus,
  taskDescriptionSchema,
  taskDueDateSchema,
  taskTitleSchema,
  type UpdateTaskRequest,
} from "@esi/shared";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { navigate, projectTaskPath } from "../router.tsx";
import { useReloadHold } from "../shell/build-refresh.ts";
import { AssigneePicker } from "./AssigneePicker.tsx";
import { TaskSelect, textAreaClass, writeError } from "./parts.tsx";
import { useTaskWrite } from "./queries.ts";

export function changedFields(
  before: {
    title?: string;
    description?: string | null;
    status: TaskStatus;
    dueDate: string | null;
    assigneeUserId: string | null;
  },
  after: UpdateTaskRequest,
): UpdateTaskRequest {
  return Object.fromEntries(
    Object.entries(after).filter(([key, value]) => value !== before[key as keyof typeof before]),
  );
}
export function TaskEditor({
  projectId,
  task,
  onClose,
}: {
  projectId: string;
  task?: TaskDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const closePeople = useRef<(() => boolean) | null>(null);
  const original = useRef(task).current;
  const [title, setTitle] = useState(task && !task.hidden ? task.title : "");
  const [description, setDescription] = useState(
    task && !task.hidden ? (task.description ?? "") : "",
  );
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? "todo");
  const [dueDate, setDue] = useState(task?.dueDate ?? "");
  const [person, setPerson] = useState<TaskPerson | null>(task?.assignee ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [discard, setDiscard] = useState(false);
  const write = useTaskWrite(projectId, task?.id);
  const rights = task?.actions;
  const text = !task || (!task.hidden && rights?.editText);
  const schedule = !task || rights?.editSchedule;
  const assign = !task || rights?.assign;
  const statusAllowed = !task || rights?.changeStatus;
  const dirty =
    title !== (original && !original.hidden ? original.title : "") ||
    description !== (original && !original.hidden ? (original.description ?? "") : "") ||
    status !== (original?.status ?? "todo") ||
    dueDate !== (original?.dueDate ?? "") ||
    person?.userId !== original?.assignee?.userId;
  useReloadHold(dirty || write.isPending);
  const close = () => {
    if (!write.isPending) {
      if (dirty) {
        setDiscard(true);
      } else {
        onClose();
      }
    }
  };
  const submit = async () => {
    const next: UpdateTaskRequest = {};
    const fieldErrors: Record<string, string> = {};
    if (text) {
      const titleResult = taskTitleSchema.safeParse(title);
      const desc = taskDescriptionSchema.safeParse(description);
      if (!titleResult.success) {
        fieldErrors.title = t("tasks.titleInvalid");
      } else {
        next.title = titleResult.data;
      }
      if (!desc.success) {
        fieldErrors.description = t("tasks.descriptionInvalid");
      } else {
        next.description = desc.data || null;
      }
    }
    if (schedule) {
      if (dueDate && !taskDueDateSchema.safeParse(dueDate).success) {
        fieldErrors.dueDate = t("tasks.dateInvalid");
      }
      next.dueDate = dueDate || null;
    }
    if (assign) {
      next.assigneeUserId = person?.userId ?? null;
    }
    if (statusAllowed) {
      next.status = status;
    }
    setErrors(fieldErrors);
    setFailure(null);
    if (Object.keys(fieldErrors).length) {
      return;
    }
    try {
      if (task && original) {
        const patch = changedFields(
          {
            ...(original.hidden
              ? {}
              : { title: original.title, description: original.description }),
            status: original.status,
            dueDate: original.dueDate,
            assigneeUserId: original.assignee?.userId ?? null,
          },
          next,
        );
        if (!Object.keys(patch).length) {
          setFailure(t("tasks.unchanged"));
          return;
        }
        await write.mutateAsync({ kind: "edit", data: patch });
        onClose();
      } else {
        const result = await write.mutateAsync({
          kind: "create",
          data: createTaskRequestSchema.parse(next),
        });
        onClose();
        if (result && "projectId" in result && "id" in result) {
          navigate(projectTaskPath(projectId, result.id));
        }
      }
    } catch (error) {
      setFailure(writeError(error, t));
    }
  };
  return (
    <>
      <AppDialog
        open
        onClose={close}
        title={t(task ? "tasks.edit" : "tasks.newTask")}
        onEscapeKeyDown={(event) => {
          if (closePeople.current?.()) {
            event.preventDefault();
          }
        }}
      >
        <form
          className="flex min-w-0 flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!write.isPending) {
              void submit();
            }
          }}
          noValidate
        >
          {text ? (
            <>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="task-title" className="text-sm font-medium">
                  {t("tasks.taskTitle")}
                </label>
                <Input
                  id="task-title"
                  autoComplete="off"
                  value={title}
                  disabled={write.isPending}
                  aria-invalid={Boolean(errors.title)}
                  aria-describedby="task-title-help"
                  onChange={(e) => setTitle(e.target.value)}
                />
                <p id="task-title-help" className="text-xs text-fg-muted tabular-nums">
                  {Array.from(normalizeTaskTitle(title)).length}/200
                </p>
                {errors.title ? <InlineError>{errors.title}</InlineError> : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="task-description" className="text-sm font-medium">
                  {t("tasks.description")}
                </label>
                <textarea
                  id="task-description"
                  className={textAreaClass}
                  value={description}
                  disabled={write.isPending}
                  aria-invalid={Boolean(errors.description)}
                  aria-describedby="task-description-help"
                  onChange={(e) => setDescription(e.target.value)}
                />
                <p id="task-description-help" className="text-xs text-fg-muted tabular-nums">
                  {Array.from(description.normalize("NFC").trim()).length}/4000
                </p>
                {errors.description ? <InlineError>{errors.description}</InlineError> : null}
              </div>
            </>
          ) : null}
          {statusAllowed ? (
            <TaskSelect
              label={t("tasks.status")}
              value={status}
              disabled={write.isPending}
              onChange={setStatus}
              options={(["todo", "in_progress", "blocked", "done"] as const).map((value) => ({
                value,
                label: t(`tasks.${value}`),
              }))}
            />
          ) : null}
          {schedule ? (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="task-due">
                {t("tasks.due")}
              </label>
              <div className="flex gap-2">
                <Input
                  id="task-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDue(e.target.value)}
                  disabled={write.isPending}
                  aria-invalid={Boolean(errors.dueDate)}
                />
                <Button
                  variant="ghost"
                  type="button"
                  disabled={!dueDate || write.isPending}
                  onClick={() => setDue("")}
                >
                  {t("tasks.clearDate")}
                </Button>
              </div>
              {errors.dueDate ? <InlineError>{errors.dueDate}</InlineError> : null}
            </div>
          ) : null}
          {assign ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">{t("tasks.assignee")}</p>
              <AssigneePicker
                projectId={projectId}
                closeList={closePeople}
                taskId={task?.id}
                value={person}
                onChange={setPerson}
                disabled={write.isPending}
              />
            </div>
          ) : null}
          {failure ? <InlineError>{failure}</InlineError> : null}
          <DialogFooter sticky>
            <Button type="button" variant="outline" disabled={write.isPending} onClick={close}>
              {t("app.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                write.isPending || Boolean(task && !text && !schedule && !assign && !statusAllowed)
              }
            >
              {write.isPending ? t("app.saving") : t("app.save")}
            </Button>
          </DialogFooter>
        </form>
      </AppDialog>
      {discard ? (
        <AppDialog
          open
          onClose={() => setDiscard(false)}
          title={t("tasks.discard")}
          description={t("tasks.discardBody")}
        >
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscard(false)}>
              {t("app.cancel")}
            </Button>
            <Button
              variant="destructive"
              className="text-accent-on dark:bg-destructive dark:hover:bg-destructive/90"
              onClick={onClose}
            >
              {t("tasks.discardAction")}
            </Button>
          </DialogFooter>
        </AppDialog>
      ) : null}
    </>
  );
}
